import { QAQC_METRIC_CATEGORY_LABELS, type QaqcMetricCategory } from '@qms360/shared';
import type { ReportPeriod } from '../../lib/api.js';
import { DerivedFigure, VarianceBadge } from './DerivedFigure.js';
import { t } from '../../lib/i18n.js';

/**
 * The Project Quality Index.
 *
 * Carries the **provisional** badge for as long as the weighting is ours rather
 * than Algihaz's. Spec §9 requires a composite index and never defines one, so
 * the numbers behind this figure were invented by the build team — and a reader
 * is entitled to know that before quoting it (Rule 6).
 *
 * It also shows which categories were excluded. A PQI computed across two active
 * categories is a different claim from one across four, and hiding that would let
 * a project look better simply for having recorded less.
 */
export function PqiCard({ period }: { period: ReportPeriod }) {
  const isProvisional = period.pqiInputs?.isProvisional !== false;
  const categories = period.pqiInputs?.categories ?? [];
  const excluded = categories.filter((entry) => !entry.active);

  return (
    <section className="rounded-md border border-border bg-surface p-5 shadow-card">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <dl className="m-0">
          <DerivedFigure
            label={t("Project Quality Index")}
            value={period.pqiValue}
            explanation={period.pqiExplanation}
            size="large"
          />
        </dl>
        <div className="text-end">
          <VarianceBadge value={period.pqiVariance} />
        </div>
      </div>

      {isProvisional && (
        <p
          role="note"
          className="mt-4 rounded-sm bg-tone-pending-surface px-3 py-2 text-xs text-content-secondary"
        >
          <strong className="font-semibold">{t("Provisional weighting.")}</strong>{' '}{t("The index formula is not defined in the approved specification. These weights are a starting point pending Algihaz confirmation, and the figure should be read with that in mind.")}
        </p>
      )}

      {categories.length > 0 && (
        <dl className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {categories.map((entry) => (
            <DerivedFigure
              key={entry.category}
              label={QAQC_METRIC_CATEGORY_LABELS[entry.category as QaqcMetricCategory] ?? entry.category}
              value={entry.active ? entry.value : null}
              explanation={entry.active ? null : 'No activity — excluded from the index.'}
            />
          ))}
        </dl>
      )}

      {/* Only meaningful when something was actually weighted. With nothing
          active there is no re-normalisation to describe, and saying "across the
          0 active categories" is noise the PQI explanation already covers. */}
      {excluded.length > 0 && excluded.length < categories.length && (
        <p className="mt-3 text-xs text-content-secondary">
          {t("Weights were re-normalised across the")}{' '}{categories.length - excluded.length}{' '}{t("active")}
          {categories.length - excluded.length === 1 ? t(" category") : t(" categories")}.
        </p>
      )}
    </section>
  );
}
